import { ShippingOption } from "./paymentService";
import { 
  collection, 
  getDocs, 
  doc,
  getDoc,
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
  updateDoc
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// API base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

export interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  onSalePrice?: number;
  onSale?: boolean;
  image: string;
  rushdTreat?: boolean;
}

export interface OrderDiscount {
  code: string;
  discountId: string;
  amount: number;
  type: 'percentage' | 'fixed' | 'free_shipping';
}

export interface Customer {
  name: string;
  email?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

export interface Shipping {
  option: ShippingOption;
  cost: number;
  priorityProcessing: boolean;
  priorityFee: number;
}

export interface Tracking {
  carrier: string;
  carrierName?: string;
  trackingNumber: string;
  url?: string;
  status: string;
  estimatedDelivery?: Date;
  lastUpdate: Date;
}

export type OrderStatus = 'pending' | 'paid' | 'processed' | 'shipped' | 'delivered' | 'cancelled' | 'payment_failed' | 'all-orders';

const ALLOWED_ALL_ORDER_STATUSES = ['paid', 'processed', 'shipped', 'delivered', 'cancelled'] as const;

export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'refund_pending' | 'refunded';

export interface Order {
  id: string;
  orderNumber: string;
  items: OrderItem[];
  customer: Customer;
  shipping: Shipping;
  payment: {
    paymentIntentId: string;
    amount: number;
    currency: string;
    status: PaymentStatus;
  };
  status: OrderStatus;
  tracking?: Tracking;
  refund?: {
    refundId: string;
    amount: number;
    currency: string;
    status: 'pending' | 'succeeded' | 'failed';
    reason?: string;
    createdAt: Date;
    processedAt?: Date;
  };
  discount?: OrderDiscount;
  subtotal: number;
  total: number;
  notes?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  // Currency metadata for accurate display
  purchaseCurrency?: string;
  exchangeRateSnapshot?: { [key: string]: number };
  displayTotal?: number;
}

/**
 * Fetch order details by id directly from Firebase
 */
export const fetchOrderById = async (orderId: string, userId?: string): Promise<Order> => {
  try {
    // Get the order document from Firebase
    const orderDocRef = doc(db, 'orders', orderId);
    const orderDocSnap = await getDoc(orderDocRef);
    
    if (!orderDocSnap.exists()) {
      throw new Error('Order not found');
    }
    
    const orderData = orderDocSnap.data();
    
    // If userId is provided, verify that this order belongs to the user
    if (userId && orderData.userId !== userId) {
      throw new Error('You are not authorized to view this order');
    }
    
    // Process the order data using the existing helper function
    const processedOrder = processOrderData(orderDocSnap as QueryDocumentSnapshot<DocumentData>);
    
    return processedOrder;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

// Pagination result type
export interface PaginatedOrdersResult {
  orders: Order[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}

/**
 * Helper function to process order data consistently
 */
const processOrderData = (doc: QueryDocumentSnapshot<DocumentData>): Order => {
  const data = doc.data();
  
  // Helper function to convert Firestore Timestamp to JavaScript Date
  const convertTimestamp = (timestamp: any): string => {
    if (!timestamp) return new Date().toISOString();
    
    // Check if it's a Firestore Timestamp object
    if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
      return timestamp.toDate().toISOString();
    }
    
    // Check if it's a Firestore Timestamp-like object with _seconds
    if (timestamp && typeof timestamp === 'object' && timestamp._seconds) {
      return new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000).toISOString();
    }
    
    // If it's already a string or Date, use it
    if (typeof timestamp === 'string') return timestamp;
    if (timestamp instanceof Date) return timestamp.toISOString();
    
    // Fallback
    return new Date().toISOString();
  };
  
  return {
    id: doc.id,
    orderNumber: data.orderNumber || doc.id,
    items: data.items || [],
    customer: data.customer || {},
    shipping: data.shipping || {},
    payment: data.payment || {},
    status: data.status || 'pending',
    tracking: data.tracking,
    refund: data.refund,
    discount: data.discount,
    subtotal: data.subtotal || 0,
    total: data.total || 0,
    notes: data.notes,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    // Include currency metadata
    purchaseCurrency: data.purchaseCurrency,
    exchangeRateSnapshot: data.exchangeRateSnapshot,
    displayTotal: data.displayTotal
  } as Order;
};

/**
 * Get the total count of orders for a given user and status
 */
export const getOrderCount = async (
  userId: string, 
  status: OrderStatus | 'all-orders' = 'all-orders'
): Promise<number> => {
  try {
    const ordersCollection = collection(db, 'orders');
    let countQuery;
    
    if (status === 'all-orders') {
      countQuery = query(
        ordersCollection,
        where('userId', '==', userId),
        where('status', 'in', ALLOWED_ALL_ORDER_STATUSES)
      );
    } else {
      countQuery = query(
        ordersCollection,
        where('userId', '==', userId),
        where('status', '==', status)
      );
    }
    
    const snapshot = await getCountFromServer(countQuery);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting order count:', error);
    throw new Error('Failed to get order count');
  }
};

/**
 * Fetch cursors for intermediate pages (used for non-sequential pagination)
 */
export const fetchOrderPageCursors = async (
  userId: string,
  targetPage: number,
  pageSize: number = 10,
  status: OrderStatus | 'all-orders' = 'all-orders'
): Promise<Map<number, QueryDocumentSnapshot<DocumentData> | null>> => {
  try {
    const cursors = new Map<number, QueryDocumentSnapshot<DocumentData> | null>();
    const ordersCollection = collection(db, 'orders');
    
    // Build base query based on status
    let baseQuery;
    if (status === 'all-orders') {
      baseQuery = query(
        ordersCollection,
        where('userId', '==', userId),
        where('status', 'in', ALLOWED_ALL_ORDER_STATUSES),
        orderBy('createdAt', 'desc'),
        orderBy('__name__')
      );
    } else {
      baseQuery = query(
        ordersCollection,
        where('userId', '==', userId),
        where('status', '==', status),
        orderBy('createdAt', 'desc'),
        orderBy('__name__')
      );
    }

    // Fetch documents up to the target page to get all intermediate cursors
    const totalDocsNeeded = (targetPage - 1) * pageSize;
    const fetchQuery = query(baseQuery, limit(totalDocsNeeded));
    
    if (totalDocsNeeded > 0) {
      const snapshot = await getDocs(fetchQuery);
      const docs = snapshot.docs;
      
      // Store cursors for each page
      for (let page = 1; page < targetPage; page++) {
        const docIndex = page * pageSize - 1; // Last doc of the page
        if (docIndex < docs.length) {
          cursors.set(page, docs[docIndex]);
        }
      }
    }
    
    return cursors;
  } catch (error) {
    console.error('Error fetching order page cursors:', error);
    throw new Error('Failed to fetch order page cursors');
  }
};

/**
 * Fetch orders with pagination support
 */
export const fetchOrdersPaginated = async (
  userId: string,
  pageSize: number = 10,
  status: OrderStatus | 'all-orders' = 'all-orders',
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<PaginatedOrdersResult> => {
  try {
    const ordersCollection = collection(db, 'orders');
    let orderQuery;
    
    // Build base query based on status
    if (status === 'all-orders') {
      orderQuery = query(
        ordersCollection,
        where('userId', '==', userId),
        where('status', 'in', ALLOWED_ALL_ORDER_STATUSES),
        orderBy('createdAt', 'desc'),
        orderBy('__name__') // Add document ID for stable ordering
      );
    } else {
      orderQuery = query(
        ordersCollection,
        where('userId', '==', userId),
        where('status', '==', status),
        orderBy('createdAt', 'desc'),
        orderBy('__name__')
      );
    }
    
    // Add pagination cursor if provided
    if (lastDoc) {
      orderQuery = query(orderQuery, startAfter(lastDoc));
    }
    
    // Add limit
    orderQuery = query(orderQuery, limit(pageSize));
    
    const snapshot = await getDocs(orderQuery);
    const orders = snapshot.docs.map(processOrderData);
    
    const lastDocResult = snapshot.docs.length > 0 
      ? snapshot.docs[snapshot.docs.length - 1] 
      : null;
    
    const hasMore = snapshot.docs.length === pageSize;
    
    return {
      orders,
      lastDoc: lastDocResult,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw new Error('Failed to fetch orders');
  }
};

/**
 * Update order with complete customer data after payment success
 */
export const updateOrderCustomerData = async (
  orderId: string, 
  customer: Customer, 
  userId: string,
  userEmail?: string
): Promise<Order> => {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/update-order-customer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        customer,
        userId,
        userEmail
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error?.message || 
        errorData.error?.details || 
        errorData.error || 
        'Failed to update order customer data'
      );
    }

    const result = await response.json();
    
    // Return the updated order data
    return result.order;
  } catch (error) {
    console.error('Error updating order customer data:', error);
    throw error;
  }
};

/**
 * Cancel an order through the backend API
 */
export const cancelOrder = async (orderId: string): Promise<void> => {
  try {
    
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to cancel order');
    }

    const result = await response.json();
  } catch (error) {
    console.error('Error cancelling order:', error);
    throw error;
  }
}; 
/**
 * Update order with complete customer data after payment failure
 */
export const updateOrderCustomerDataFailed = async (
  orderId: string, 
  customer: Customer, 
  userId: string,
  userEmail?: string
): Promise<Order> => {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/update-order-customer-failed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        customer,
        userId,
        userEmail
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error?.message || 
        errorData.error?.details || 
        errorData.error || 
        'Failed to update failed payment order customer data'
      );
    }

    const result = await response.json();
    
    // Return the updated order data
    return result.order;
  } catch (error) {
    console.error('Error updating failed payment order customer data:', error);
    throw error;
  }
}; 
